import { VStack } from '@chakra-ui/react';
import { DateInput } from 'Atoms';
import {
  EsrsAssessmentDocument_,
  EsrsAssessmentsDocument_,
  useUpsertEsrsAssessmentMutation,
  useUpsertReportingUnitLineageMutation,
} from 'models';
import { Modal, Select } from 'Molecules';
import { useEffect, useMemo } from 'react';
import { Controller, useForm, useFormState } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { Typography } from 'Tokens';
import { useCurrentCompanyId, useToast } from 'utils/hooks';
import { useTranslation } from 'utils/translation';
import { EsrsAssessmentEditFields } from './SustainabilityAssessmentList';
import { useAuth } from 'Providers/auth/AuthProvider.tsx';
import { FrameworkOptions } from './SustainabilityAssessment.d';

type EsrsAssessmentFormFields = {
  reportingYear: Date;
  frameworkRef: string;
};

export const SustainabilityAssessmentModal = ({
  isOpen,
  onClose,
  editFields,
  companyFrameworks,
}: {
  isOpen: boolean;
  onClose: () => void;
  editFields?: EsrsAssessmentEditFields;
  companyFrameworks: string[];
}) => {
  const { t } = useTranslation(['common', 'esrs']);
  const toast = useToast();
  const { companyId } = useCurrentCompanyId();
  const { user: currentUser } = useAuth();
  const navigate = useNavigate();
  const [upsertEsrsAssessment] = useUpsertEsrsAssessmentMutation();
  const [upsertReportingUnitLineage] = useUpsertReportingUnitLineageMutation();
  const { control, handleSubmit, reset } = useForm<EsrsAssessmentFormFields>({
    mode: 'all',
    reValidateMode: 'onBlur',
  });
  const { isValid } = useFormState({ control });

  useEffect(() => {
    reset({
      reportingYear: editFields ? (editFields.reportingYear ?? new Date()) : new Date(),
    });
  }, [isOpen]);

  const accessibleFrameworks = useMemo(
    () => FrameworkOptions.filter((framework) => companyFrameworks.includes(framework.value)),
    [FrameworkOptions, companyFrameworks]
  );

  const onSubmit = (values: EsrsAssessmentFormFields) => {
    const selectedFramework =
      companyFrameworks.length > 1 ? values.frameworkRef : companyFrameworks?.[0];

    upsertEsrsAssessment({
      variables: {
        esrsAssessment: {
          id: editFields?.id,
          reportingYear: new Date(values.reportingYear).getUTCFullYear(),
          companyId: companyId,
          companySize: 'small',
          projectLeaderId: currentUser?.id,
          frameworkRef: selectedFramework,
        },
      },
      onError: (error) => {
        if (error.message.includes('Uniqueness violation')) {
          toast({
            text: 'An assessment for this year already exists',
            variant: 'danger',
          });
        } else {
          toast({
            text: 'Failed to create assessment',
            variant: 'danger',
          });
        }
      },
      onCompleted: (data) => {
        navigate(`${selectedFramework}/${data?.insert_EsrsAssessment_one?.id}/onboarding`);
        onClose();
      },
      refetchQueries: [EsrsAssessmentsDocument_],
    }).then((res) => {
      upsertReportingUnitLineage({
        variables: {
          reportingUnitLineage: {
            companyId,
            isCompanyLevel: true,
            reportingUnits: {
              data: [
                {
                  name: 'Company level',
                  assessmentId: res.data?.insert_EsrsAssessment_one?.id,
                  isCompanyLevel: true,
                },
              ],
            },
          },
        },
        refetchQueries: [EsrsAssessmentDocument_],
      });
    });
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t('common:assessment.createAssessment')}
      confirmButtonProps={{
        type: 'submit',
        form: 'esrs-assessment-form',
        isDisabled: !isValid,
      }}
    >
      <form onSubmit={handleSubmit(onSubmit)} id="esrs-assessment-form">
        <VStack spacing="16px">
          <VStack alignItems="stretch" width="100%">
            <Typography variant="bodyStrong">{t('esrs:assessment.form.reportingDate')}</Typography>
            <Controller
              name="reportingYear"
              control={control}
              render={({ field }) => (
                <DateInput
                  width="100%"
                  value={field.value}
                  setValue={(date) => field.onChange(date ?? new Date())}
                  showYearPicker
                  dateFormat="yyyy"
                />
              )}
            />
            {companyFrameworks.length > 1 && (
              <VStack alignItems="stretch" mt="8px">
                <Typography variant="bodyStrong">Framework</Typography>
                <Controller
                  name="frameworkRef"
                  control={control}
                  render={({ field }) => (
                    <Select<{ value: string; label: string }>
                      width="200px"
                      value={accessibleFrameworks.find((opt) => opt.value === field.value)}
                      options={accessibleFrameworks}
                      onChange={(opt) => {
                        field.onChange(opt?.value);
                      }}
                    />
                  )}
                />
              </VStack>
            )}
          </VStack>
        </VStack>
      </form>
    </Modal>
  );
};
